export interface ScheduleDep {
  predecessorTaskId: number
  successorTaskId: number
  dependencyType: string
}

// 공종 순서별 색상 팔레트 — 디자인 토큰셋에 없는 차트용 색이라 D-08 §6.5에 따라 hex로 직접
// 관리한다(간트/캘린더/리스트/공유뷰가 공통으로 참조 — 한 곳만 바꾸면 전체에 반영됨).
export const WORK_TYPE_COLORS = [
  '#1471A8', '#B91C1C', '#B45309', '#1D4ED8', '#6D28D9', '#C2410C',
  '#15803D', '#9D174D', '#7C3AED', '#92400E', '#0F766E', '#BE185D',
]

// "보기"(스케줄/캘린더/리스트) 세그먼트 옵션 — 간트/캘린더/리스트 3개 화면의 옵션바가 공통으로
// 참조한다. 각 화면은 자신의 view만 value로 넘기고, onChange에서 다른 값이면 navigate한다.
export const SCHEDULE_VIEW_OPTIONS: { label: string; value: string }[] = [
  { label: '스케줄', value: 'gantt' },
  { label: '캘린더', value: 'cal-b2' },
  { label: '리스트', value: 'work-type' },
]

export function addDays(d: Date, n: number): Date {
  const r = new Date(d)
  r.setDate(r.getDate() + n)
  return r
}

export function formatISO(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export function daysBetween(a: Date, b: Date): number {
  return Math.round((b.getTime() - a.getTime()) / 86400000)
}

// ── 작업일 계산 (A-07) ─────────────────────────────────────────────────────────────────
// BE `WorkingDayCalculator`와 같은 규칙: 토요일·일요일·공휴일(SAT/SUN/PUBLIC/SUBSTITUTE)은 비작업일(2026-10-28부터 토요일 포함),
// 종료일 포함. holidays = BE `GET /schedule/holidays` 응답의 날짜 집합.
// 규칙 일치는 scheduleUtils.test.ts의 공통 케이스 표(BE WorkingDayCalculatorTest와 같은 내용)로 고정한다.
// 비작업일 집합이 조회 범위를 벗어난 날짜에는 쓰지 않는다 — 계산 전에 isHolidayCovered로 확인할 것.

/** 작업일 탐색 상한(일). 휴일 집합이 비정상적으로 큰 경우의 무한 루프 방지용. */
const WORKDAY_SCAN_LIMIT = 3660

export function isNonWorkday(d: Date, holidays: Set<string>): boolean {
  // 토·일요일은 휴일 집합과 무관하게 비작업일(서버 집합에도 SAT·SUN 이 들어 있다)
  return d.getDay() === 0 || d.getDay() === 6 || holidays.has(formatISO(d))
}

/** d가 작업일이면 d, 비작업일이면 다음 작업일(BE nextWorkingDayOnOrAfter). */
export function nextWorkday(d: Date, holidays: Set<string>): Date {
  let r = d
  for (let i = 0; isNonWorkday(r, holidays); i++) {
    if (i >= WORKDAY_SCAN_LIMIT) throw new RangeError('다음 작업일을 찾지 못했습니다')
    r = addDays(r, 1)
  }
  return r
}

/** start~end 양끝 포함 작업일 수. end < start 는 거부한다(0으로 대체하지 않음). */
export function countWorkdays(start: Date, end: Date, holidays: Set<string>): number {
  if (daysBetween(start, end) < 0) throw new RangeError('종료일이 시작일보다 앞설 수 없습니다')
  let n = 0
  for (let r = start; daysBetween(r, end) >= 0; r = addDays(r, 1)) {
    if (!isNonWorkday(r, holidays)) n++
  }
  return n
}

/** start 포함 n작업일째 날(= 종료일, BE calcEndDate). n < 1 은 거부한다. */
export function addWorkdays(start: Date, n: number, holidays: Set<string>): Date {
  if (!Number.isInteger(n) || n < 1) throw new RangeError('작업일 수는 1 이상의 정수여야 합니다')
  let r = start
  let counted = 0
  for (let i = 0; ; i++) {
    if (i > WORKDAY_SCAN_LIMIT) throw new RangeError('작업일 계산 범위를 초과했습니다')
    if (!isNonWorkday(r, holidays)) counted++
    if (counted >= n) return r
    r = addDays(r, 1)
  }
}

export interface HolidayCoverage { from: string; to: string }

/** 모든 날짜가 공휴일 조회 범위 [from, to] 안인지(YYYY-MM-DD 문자열 비교). */
export function isHolidayCovered(range: HolidayCoverage, ...dates: Date[]): boolean {
  return dates.every(d => {
    const iso = formatISO(d)
    return iso >= range.from && iso <= range.to
  })
}

// 기존 이름 유지용(호출처: 캘린더·리스트·간트 후행 계산). 방향 인자는 쓰지 않는다 —
// 밀기든 당기기든 항상 다음 작업일로 넘긴다(토·일요일·공휴일 모두). 신규 코드는 nextWorkday를 쓴다.
export function adjustToWorkday(d: Date, holidays: Set<string>, _direction: number): Date {
  return nextWorkday(d, holidays)
}

export function getCascadeIds(taskId: number, deps: ScheduleDep[]): number[] {
  const succMap = new Map<number, number[]>()
  for (const d of deps) {
    if (d.dependencyType !== 'FS') continue
    const list = succMap.get(d.predecessorTaskId) ?? []
    list.push(d.successorTaskId)
    succMap.set(d.predecessorTaskId, list)
  }
  const result = [taskId]
  const visited = new Set([taskId])
  let i = 0
  while (i < result.length) {
    const succs = succMap.get(result[i]) ?? []
    for (const s of succs) {
      if (!visited.has(s)) { visited.add(s); result.push(s) }
    }
    i++
  }
  return result
}

export interface CascadeTaskLike {
  taskId: number
  workTypeName: string
  startDate: Date
  endDate: Date
  /** 이 공정이 끝난 후 바로 다음 공정이 시작되기 전까지 반드시 대기해야 하는 기간(대기일).
   *  선행-후행 "관계"가 아니라 그 공정 자체에 붙는 값이다(예: 콘크리트 양생 등) — BE는
   *  TM_WORK_TYPE_DEPENDENCY 기반으로 스케줄 생성 시 tasks[].leadTime에 고정해서 내려준다. */
  leadTime: number
}

export type CascadePlan = Map<number, { startDate: Date; endDate: Date }>

// 캐스케이드 재배치 핵심 엔진 — anchor(사용자가 직접 바꾼 공종)는 새 날짜를 그대로 확정하고,
// 나머지 후행 공종은 BFS 순서대로 "선행 종료일 + 1일 + 선행 공정의 leadTime(대기일)"을 계산해
// 새 자리를 정한다(같은 태스크로 들어오는 선행이 여럿이면 그중 가장 늦게 끝나는 쪽이 기준).
// 공휴일·일요일은 adjustToWorkday로 넘긴다. 기간(소요일)은 작업일 수 기준으로 원래 그대로 유지된다(A-09).
//
// mode: 'always'          — 무조건 재계산해서 이동(당기기 확인 후 "함께 이동" 선택 시)
//       'push-if-violated' — 현재 위치가 이미 필요 시작일 이상(=여유일을 안 건드림)이면 그 태스크와
//                             하위 브랜치는 손대지 않고 그대로 둔다(밀기 시 자동 적용).
export function computeCascadeReschedule(
  anchorId: number,
  anchorStart: Date,
  anchorEnd: Date,
  tasks: CascadeTaskLike[],
  deps: ScheduleDep[],
  holidays: Set<string>,
  mode: 'always' | 'push-if-violated',
): CascadePlan {
  const cascadeIds = getCascadeIds(anchorId, deps)
  const cascadeSet = new Set(cascadeIds)
  const plan: CascadePlan = new Map()
  plan.set(anchorId, { startDate: anchorStart, endDate: anchorEnd })

  for (let i = 1; i < cascadeIds.length; i++) {
    const id = cascadeIds[i]
    const task = tasks.find(t => t.taskId === id)
    if (!task) continue
    // 후행 기간 보존 — 달력일 간격이 아니라 작업일 수(A-09). 시작이 비작업일로 밀려도 작업일 수는 같다.
    const workdays = Math.max(1, countWorkdays(task.startDate, task.endDate, holidays))

    const predEdges = deps.filter(d =>
      d.successorTaskId === id && d.dependencyType === 'FS' && cascadeSet.has(d.predecessorTaskId))

    let requiredStart: Date | null = null
    for (const edge of predEdges) {
      const predPlan = plan.get(edge.predecessorTaskId)
      if (!predPlan) continue
      const predTask = tasks.find(t => t.taskId === edge.predecessorTaskId)
      const candidate = addDays(predPlan.endDate, 1 + (predTask?.leadTime || 0))
      if (!requiredStart || candidate.getTime() > requiredStart.getTime()) requiredStart = candidate
    }

    if (!requiredStart) {
      plan.set(id, { startDate: task.startDate, endDate: task.endDate })
      continue
    }
    if (mode === 'push-if-violated' && task.startDate.getTime() >= requiredStart.getTime()) {
      // 이미 여유일 이상 떨어져 있음 — 건드리지 않고 그대로 둔다(하위 후행공정도 이 위치 기준으로 판단)
      plan.set(id, { startDate: task.startDate, endDate: task.endDate })
      continue
    }

    const shiftDir = requiredStart.getTime() >= task.startDate.getTime() ? 1 : -1
    const adjustedStart = adjustToWorkday(requiredStart, holidays, shiftDir)
    plan.set(id, { startDate: adjustedStart, endDate: addWorkdays(adjustedStart, workdays, holidays) })
  }

  return plan
}

// ── 일수 이동 모드 (A-13) ───────────────────────────────────────────────────────────────
// 기간 수정 팝오버의 '후행 공정도 같이 이동'용. computeCascadeReschedule(당겨 붙임)와 달리
// 후행 간의 기존 간격을 보존한다.
//  ① 기준 공종: 팝오버 입력값. 시작일이 비작업일이면 다음 작업일로 보정(종료일은 입력값 유지, 시작보다 앞서면 시작일로).
//  ② 이동량 = (기준 공종 새 종료일 − 기존 종료일)의 달력일 차.
//  ③ 후행 전체(간접 후행 포함)의 시작일에 같은 달력일을 더하고, 비작업일이면 다음 작업일로 보정,
//     종료일은 기존 작업일 수를 유지해 addWorkdays로 산출.
//  ④ 후행끼리·선행과의 기존 간격은 그대로 유지. ⑤ '가장 늦은 선행 기준' 재배치는 하지 않는다.
export function computeShiftReschedule(
  anchorId: number,
  newStart: Date,
  newEnd: Date,
  tasks: CascadeTaskLike[],
  deps: ScheduleDep[],
  holidays: Set<string>,
): CascadePlan {
  const anchor = tasks.find(t => t.taskId === anchorId)
  if (!anchor) throw new Error(`기준 공종을 찾을 수 없습니다: ${anchorId}`)

  const anchorStart = nextWorkday(newStart, holidays)
  const anchorEnd = daysBetween(anchorStart, newEnd) < 0 ? anchorStart : newEnd
  const plan: CascadePlan = new Map()
  plan.set(anchorId, { startDate: anchorStart, endDate: anchorEnd })

  const shiftDays = daysBetween(anchor.endDate, anchorEnd)
  const cascadeIds = getCascadeIds(anchorId, deps)
  for (let i = 1; i < cascadeIds.length; i++) {
    const task = tasks.find(t => t.taskId === cascadeIds[i])
    if (!task) continue
    const workdays = Math.max(1, countWorkdays(task.startDate, task.endDate, holidays))
    const start = nextWorkday(addDays(task.startDate, shiftDays), holidays)
    plan.set(task.taskId, { startDate: start, endDate: addWorkdays(start, workdays, holidays) })
  }
  return plan
}

// ── 선행 경고 (A-14) ────────────────────────────────────────────────────────────────────
export interface PredecessorWarning {
  taskId: number
  predecessorTaskId: number
  predecessorName: string
  /** 문구에 표기하는 선행 종료일(기획 원문: 대기일 반영 전 선행 종료 기준일) */
  predecessorEnd: Date
  message: string
}

// 계획(plan)에 든 공종의 모든 선행(FS)을 검사한다. 기준: 후행 시작일 < 선행 종료일 + 1 + 선행 대기일
// (그 날이 비작업일이면 다음 작업일까지 반영 — 후행 시작 규칙과 동일). 계획 안의 선행은 새 계획
// 날짜, 밖의 선행은 현재 날짜로 비교한다. 적용을 막지 않고 경고만 돌려준다.
export function predecessorWarnings(
  plan: CascadePlan,
  tasks: CascadeTaskLike[],
  deps: ScheduleDep[],
  holidays: Set<string>,
): PredecessorWarning[] {
  const byId = new Map(tasks.map(t => [t.taskId, t]))
  const out: PredecessorWarning[] = []
  const seen = new Set<string>()
  for (const [taskId, { startDate }] of plan) {
    for (const dep of deps) {
      if (dep.successorTaskId !== taskId || dep.dependencyType !== 'FS') continue
      const key = `${taskId}:${dep.predecessorTaskId}`
      if (seen.has(key)) continue
      const pred = byId.get(dep.predecessorTaskId)
      if (!pred) continue
      seen.add(key)
      const predEnd = plan.get(pred.taskId)?.endDate ?? pred.endDate
      const earliest = nextWorkday(addDays(predEnd, 1 + (pred.leadTime || 0)), holidays)
      if (daysBetween(startDate, earliest) > 0) {
        out.push({
          taskId,
          predecessorTaskId: pred.taskId,
          predecessorName: pred.workTypeName,
          predecessorEnd: predEnd,
          message: `선행 '${pred.workTypeName}' 종료 ${predEnd.getMonth() + 1}/${predEnd.getDate()} 이후로만 시작할 수 있어요`,
        })
      }
    }
  }
  return out
}

// 경고(warning): 공사 종료일 초과 — 저장은 허용
export function validateScheduleEndWarn(
  plan: CascadePlan,
  tasks: CascadeTaskLike[],
  scheduleEnd: Date | null,
): string | null {
  if (!scheduleEnd) return null
  for (const [taskId, { endDate }] of plan) {
    if (endDate.getTime() > scheduleEnd.getTime()) {
      const task = tasks.find(t => t.taskId === taskId)
      return `${task?.workTypeName ?? ''}의 종료일이 공사 기간을 초과합니다`
    }
  }
  return null
}

// 경고(warning): 캐스케이드 밖의(무관한) 선행 공종보다 앞서게 되는 경우 — 저장은 허용
export function validatePredecessorBlock(
  plan: CascadePlan,
  tasks: CascadeTaskLike[],
  deps: ScheduleDep[],
): string | null {
  const cascadeSet = new Set(plan.keys())
  for (const [taskId, { startDate }] of plan) {
    // 선행은 여러 개일 수 있으므로 모든 FS 선행을 검사한다
    for (const predDep of deps) {
      if (predDep.successorTaskId !== taskId || predDep.dependencyType !== 'FS' || cascadeSet.has(predDep.predecessorTaskId)) continue
      const pred = tasks.find(t => t.taskId === predDep.predecessorTaskId)
      if (pred && startDate.getTime() <= pred.endDate.getTime()) {
        const task = tasks.find(t => t.taskId === taskId)
        return `${task?.workTypeName ?? ''}은(는) 선행 공종 완료 이후로만 이동할 수 있습니다`
      }
    }
  }
  return null
}

// 선행공종 후보 목록 — currentTaskId 자신과 그로부터 도달 가능한 모든 후행(taskId)을 제외한다.
// 후행을 다시 선행으로 고르면 순환(A→B→A)이 생기므로, getCascadeIds가 이미 계산하는
// "자신 + 도달 가능한 후행 전체" 집합을 그대로 배제 기준으로 재사용한다. BE도 순환 선행 지정을
// 400 INVALID_PREDECESSOR로 거절하지만(TaskService.assertNoPredecessorCycle) 후보 목록에서
// 미리 빼서 선택 자체를 막는다.
// tasks 배열 순서 = 화면에 위→아래로 나열되는 순서(간트 행 순서·리스트 행 순서와 동일)이므로,
// currentTaskId보다 뒤(=화면에서 아래)에 있는 공종은 후보에서 제외한다(현행 유지, 완화하지 않음).
// 동일 공종이 여러 바(구간)로 나뉘어 있으면(groupTasksIntoRows 참조) 공종명당 하나(tasks에서 더 나중에
// 나오는 구간)만 남긴다.
// 단, 현재 저장돼 있는 선행(deps의 FS 선행)은 위 순서·공종명 제한과 무관하게 항상 목록에 포함한다 —
// 간트 드래그로 만든 다중 선행이 상세창 저장 때 줄어들지 않게 하기 위함(자신·후행은 제외 유지).
export function getPredecessorCandidates<T extends { taskId: number; workTypeName: string }>(
  currentTaskId: number, tasks: T[], deps: ScheduleDep[],
): T[] {
  const excluded  = new Set(getCascadeIds(currentTaskId, deps))
  const currentIdx = tasks.findIndex(t => t.taskId === currentTaskId)
  const eligible = tasks.filter((t, idx) => !excluded.has(t.taskId) && (currentIdx === -1 || idx < currentIdx))
  const byName = new Map<string, T>()
  for (const t of eligible) byName.set(t.workTypeName, t)
  const picked = new Set<number>([...byName.values()].map(t => t.taskId))
  for (const d of deps) {
    if (d.successorTaskId === currentTaskId && d.dependencyType === 'FS' && !excluded.has(d.predecessorTaskId)) {
      picked.add(d.predecessorTaskId)
    }
  }
  return tasks.filter(t => picked.has(t.taskId))
}

// ── 상세창 선행공종 저장(B-02·B-03) ─────────────────────────────────────────────────────
// PATCH /schedule/tasks/{id} 는 predecessorTaskIds(전체 목록)를 받고, updatePredecessor=true 이면 기존 선행을
// 전부 지우고 받은 목록으로 바꾼다. 상세창은 항상 선택된 선행 전체를 보내므로 간트에서 만든 다중 선행이 줄지 않는다.
export function buildPredecessorPatch(ids: number[]): { predecessorTaskIds: number[]; updatePredecessor: true } {
  return { predecessorTaskIds: [...new Set(ids)], updatePredecessor: true }
}

// 선행 목록 비교(순서 무관) — 상세창 편집중(dirty) 판정에 쓴다.
export function samePredecessorSet(a: number[], b: number[]): boolean {
  const x = [...new Set(a)].sort((p, q) => p - q)
  const y = [...new Set(b)].sort((p, q) => p - q)
  return x.length === y.length && x.every((v, i) => v === y[i])
}

// ── 간트 연결선(커넥터) 다중 선행 ───────────────────────────────────────────────────────
// 공종의 현재 선행 목록(FS) — deps 순서 유지
export function predecessorsOf(deps: ScheduleDep[], successorTaskId: number): number[] {
  return deps.filter(d => d.successorTaskId === successorTaskId && d.dependencyType === 'FS').map(d => d.predecessorTaskId)
}

// 연결 추가 — 기존 선행은 보존하고 새 선행을 덧붙인다. 이미 있으면 목록을 그대로 돌려준다(중복 추가 금지).
export function addPredecessorId(current: number[], add: number): number[] {
  return current.includes(add) ? [...current] : [...current, add]
}

// 연결 삭제 — 선택한 선행 한 개만 뺀다(나머지 보존).
export function removePredecessorId(current: number[], remove: number): number[] {
  return current.filter(id => id !== remove)
}

// 서버가 순환 선행을 거절하면 400 INVALID_PREDECESSOR (TaskService.assertNoPredecessorCycle)
export const INVALID_PREDECESSOR_MESSAGE = '선행공종이 서로 꼬여서 저장할 수 없어요. 선택을 바꿔 주세요.'

export function isInvalidPredecessorError(err: unknown): boolean {
  const code = (err as { response?: { data?: { code?: string } } } | null | undefined)?.response?.data?.code
  return code === 'INVALID_PREDECESSOR'
}

// taskId 자신 + 그로부터 역방향(후행→선행)으로 도달 가능한 모든 조상(선행) taskId.
// getCascadeIds가 "자신 + 도달 가능한 후행 전체"를 구하는 것의 반대 방향 — "이 공종 뒤에 추가"
// 처럼 새 공종을 현재 공종의 후행으로 붙일 때, 이미 현재 공종보다 앞서는(선행인) 공종을
// 그대로 다시 뒤에 붙이면 같은 공종이 앞뒤로 겹쳐 순환 성격을 띠므로 후보에서 제외하는 데 쓴다.
export function getAncestorIds(taskId: number, deps: ScheduleDep[]): number[] {
  const predMap = new Map<number, number[]>()
  for (const d of deps) {
    if (d.dependencyType !== 'FS') continue
    const list = predMap.get(d.successorTaskId) ?? []
    list.push(d.predecessorTaskId)
    predMap.set(d.successorTaskId, list)
  }
  const result = [taskId]
  const visited = new Set([taskId])
  let i = 0
  while (i < result.length) {
    const preds = predMap.get(result[i]) ?? []
    for (const p of preds) {
      if (!visited.has(p)) { visited.add(p); result.push(p) }
    }
    i++
  }
  return result
}

export interface GanttRow<T extends { taskId: number; workTypeCode: string; workTypeName: string; startDate: Date }> {
  workTypeCode: string
  workTypeName: string
  bars: T[]
}

// 동일한 workTypeCode(공종)가 여러 번 등록된 경우 새 행을 만들지 않고 한 행에 바를 여러 개
// 넣기 위한 그룹핑. 행 순서는 workTypeCode가 tasks 배열에 처음 등장한 순서를 그대로 따른다
// (이미 displayOrder/insert-after 재정렬이 반영된 순서라서 날짜순으로 다시 정렬하지 않는다).
// 행 내부 bars는 startDate 오름차순 — 좌측 컬럼(공종명·소요일 등) 표시 기준이 되는 "첫 바"를
// 결정하기 위함. 라이브 렌더(GanttView.tsx)와 PDF export가 이 함수를 그대로 공유해서 두 곳의
// 그룹핑 로직이 어긋나지 않게 한다.
export function groupTasksIntoRows<T extends { taskId: number; workTypeCode: string; workTypeName: string; startDate: Date }>(
  tasks: T[],
): GanttRow<T>[] {
  const rows: GanttRow<T>[] = []
  const rowByCode = new Map<string, GanttRow<T>>()
  for (const t of tasks) {
    let row = rowByCode.get(t.workTypeCode)
    if (!row) {
      row = { workTypeCode: t.workTypeCode, workTypeName: t.workTypeName, bars: [] }
      rowByCode.set(t.workTypeCode, row)
      rows.push(row)
    }
    row.bars.push(t)
  }
  for (const row of rows) row.bars.sort((a, b) => a.startDate.getTime() - b.startDate.getTime())
  return rows
}

// 공휴일 조회 범위(A-08): 공사 기간 시작 −1개월 ~ 종료 +1년. 작업일 계산이 가시 범위 밖으로
// 나가도 휴일이 반영되게 넓게 잡는다. 결과는 YYYY-MM-DD 문자열(useHolidayRange 인자).
export function holidayQueryRange(siteStart: Date, siteEnd: Date): HolidayCoverage {
  const from = new Date(siteStart)
  from.setMonth(from.getMonth() - 1)
  const to = new Date(siteEnd)
  to.setFullYear(to.getFullYear() + 1)
  return { from: formatISO(from), to: formatISO(to) }
}

// ── 공휴일 조회 상태 관문 (A-08) ────────────────────────────────────────────────────────
// 작업일 계산 전에 확인한다. 조회 중·실패·범위 밖이면 계산하지 않고 문구를 돌려준다(빈 집합으로 대체 금지).
export interface HolidayGate {
  status: 'idle' | 'loading' | 'success' | 'error'
  from: string | null
  to: string | null
}

/** null 이면 계산해도 됨. 문자열이면 계산을 막고 그 문구를 보여준다. dates = 계산에 쓰일 모든 날짜. */
export function holidayGateError(gate: HolidayGate, ...dates: Date[]): string | null {
  if (gate.status === 'error') return '공휴일 정보를 불러오지 못해 날짜를 옮길 수 없어요'
  if (gate.status === 'loading') return '공휴일 정보를 불러오는 중이에요. 잠시 후 다시 해 주세요'
  if (gate.status !== 'success' || !gate.from || !gate.to) return '공휴일 정보가 없어 날짜를 옮길 수 없어요'
  if (!isHolidayCovered({ from: gate.from, to: gate.to }, ...dates)) return '공휴일 정보를 확인한 기간 밖의 날짜예요'
  return null
}

// ── 끌기·리사이즈 결과 (A-09·A-10) ──────────────────────────────────────────────────────
// 막대 끌기: 이동한 시작일이 비작업일이면 다음 작업일로 보정하고, 소요일(작업일 수)은 그대로 유지한다.
export function movedRange(start: Date, end: Date, offset: number, holidays: Set<string>): { start: Date; end: Date; days: number } {
  const newStart = nextWorkday(addDays(start, offset), holidays)
  const days = Math.max(1, countWorkdays(start, end, holidays))
  return { start: newStart, end: addWorkdays(newStart, days, holidays), days }
}

// 막대 오른쪽 끝 리사이즈: 오프셋(달력일)만큼 옮긴 날짜까지의 작업일 수(최소 1)가 새 소요일, 종료일은 그 작업일 수의 마지막 날.
// 기존 시작일이 비작업일이면 A-10 규칙으로 다음 작업일로 보정한다(BE 가 비작업일 시작을 거부하므로).
export function resizedRange(start: Date, end: Date, offset: number, holidays: Set<string>): { start: Date; end: Date; days: number } {
  const s = nextWorkday(start, holidays)
  const rawEnd = addDays(end, offset)
  const target = daysBetween(s, rawEnd) < 0 ? s : rawEnd
  const days = Math.max(1, countWorkdays(s, target, holidays))
  return { start: s, end: addWorkdays(s, days, holidays), days }
}

/** 'M/D' 표기 */
export function fmtMD(d: Date): string {
  return `${d.getMonth() + 1}/${d.getDate()}`
}

/**
 * 표시용 소요일(작업일 수). 공휴일 조회 중·실패·범위 밖이거나 종료<시작이면 null — 달력일 수로 대체하지 않는다(A-08·A-09).
 * 호출측은 null 을 '–' 로 보여준다.
 */
export function workdaysOrNull(gate: HolidayGate, holidays: Set<string>, start: Date, end: Date): number | null {
  if (holidayGateError(gate, start, end) !== null) return null
  if (daysBetween(start, end) < 0) return null
  return countWorkdays(start, end, holidays)
}
